import React, {useMemo} from "react";
import {FormattedMessage} from "react-intl";
import {toDecimal} from "dinero.js";
import {AddToCartProduct, AddToCartProductSideDish} from "@/data/entities/product";
import {Product} from "@/data/entities/menu";
import {ProductOption} from "@/data/entities/option";
import {cartProductToTotalDinero, isLoyaltyReward} from "@/data/entities/cart/utils";
import {formatMoney, getFormattedProductPrice} from "@/utils/MoneyUtils";
import {useFulfillmentType} from "@widgets/pos/order/hooks/UseFulfillmentType";
import useMenu from "@widgets/pos/order/hooks/MenuProvider";
import {Button, Icon, Spinner} from "peggirkit";
import PlusMinusNumberField from "@widgets/input/plusMinusNumberField/PlusMinusNumberField";
import {TrashIcon} from "@heroicons/react/16/solid";

type OptionLine = {
    key: string,
    name: string,
    price: string,
};

type Props = {
    isPending: boolean,
    product: AddToCartProduct,
    onChangeCount: (count: number) => void,
    onRemove: () => void,
    maxCount?: number, // Only for products redeemed with loyalty points: can't be increased
};

const ProductInCartLine = ({isPending, product, onChangeCount, onRemove, maxCount}: Props) => {
    const fulfillmentType = useFulfillmentType();
    const {lookup, menuLoading} = useMenu();
    const menuProduct: Product | undefined = lookup.products.get(product.externalId);
    const options: OptionLine[] = useMemo(
        () => product.sideDishes.map((sideDish: AddToCartProductSideDish) => {
            const option: ProductOption | undefined = lookup.options.get(sideDish.externalId);
            return {
                key: `${sideDish.optionGroupExternalId}:${sideDish.externalId}`,
                name: option?.name ?? sideDish.externalId,
                price: option ? getFormattedProductPrice(option, fulfillmentType) : "0",
            };
        }),
        [product.sideDishes, lookup.options, fulfillmentType]);
    const totalPrice: string = useMemo(
        () => toDecimal(cartProductToTotalDinero(product, lookup, fulfillmentType)),
        [product, lookup, fulfillmentType]);

    return (
        <li className={"w-full flex flex-col items-start gap-1 border-b border-gray-200 dark:border-slate-800 pb-3"}>
            <div className={"w-full flex flex-row items-end gap-3 justify-between"}>
                <p className={"font-medium text-gray-900 dark:text-white"}>
                    {`${product.count}× `}
                    {
                        menuLoading
                            ? <Spinner/>
                            : menuProduct?.name ?? <FormattedMessage id={"unknownProduct"}/>
                    }
                </p>

                <data
                    className={"shrink-0 font-semibold tabular-nums whitespace-nowrap text-gray-900 dark:text-white"}
                >
                    {formatMoney(Number(totalPrice))}
                </data>
            </div>

            {
                isLoyaltyReward(product) &&
                <p className={"text-sm font-medium text-green-700 dark:text-green-400"}>
                    <FormattedMessage id={"redeemedWithLoyaltyPoints"}/>
                </p>
            }

            {
                options.length > 0 &&
                <ul className={"w-full flex flex-col text-sm text-gray-600 dark:text-slate-400"}>
                    {options.map((option: OptionLine) =>
                        <li
                            key={option.key}
                            className={"w-full flex flex-row justify-between items-end gap-3"}
                        >
                            <p>
                                {option.name}
                            </p>

                            <data>
                                {option.price}
                            </data>
                        </li>
                    )}
                </ul>
            }

            <div className={"w-full flex flex-row items-end justify-start gap-4 mt-1"}>
                <PlusMinusNumberField
                    label={<FormattedMessage id={"amountToOrder"}/>}
                    disabled={isPending || menuLoading}
                    value={product.count}
                    onChange={(newVal: number) => onChangeCount(newVal)}
                    max={maxCount}
                />

                <Button
                    iconBefore={<Icon icon={TrashIcon}/>}
                    iconOnly={true}
                    variant={"text"}
                    color={"danger"}
                    borderRadius={"rounded-full"}
                    buttonAction={{
                        as: "button",
                        actionProps: {
                            onClick: e => {
                                e.preventDefault();
                                onRemove();
                            },
                        },
                    }}
                >
                    <FormattedMessage id={"delete"}/>
                </Button>
            </div>
        </li>
    );
};

export default ProductInCartLine;